import { FormEvent } from 'react';
import { GroupEditorModal } from '@/components/group-editor/GroupEditorModal';
import { TableCheckboxData, TableColumn } from '@/ui/Table';

type GroupEditorModalWrapperTarget = {
    name: string;
};

export type GroupEditorModalWrapperProps<TItem, TTarget extends GroupEditorModalWrapperTarget> = {
    openedGroupEditor: TTarget | null;
    groupEditorData: unknown | null;
    groupEditorNameDraft: string;
    setGroupEditorNameDraft: (value: string) => void;
    isGroupEditorLoading: boolean;
    groupEditorError: string | null;
    pickedItems: string[];
    page: number;
    setPage: (page: number) => void;
    rowsOnPage: number;
    onRowsOnPageUpdate: (count: number) => void;
    isGroupEditorNameSaving: boolean;
    isGroupEditorUngrouping: boolean;
    normalizedGroupEditorNameDraft: string;
    hasGroupEditorNameChanged: boolean;
    totalRows: number;
    pageCount: number;
    pagedItems: TItem[];
    isGroupEditorHeaderChecked: boolean;
    groupEditorCheckboxData: TableCheckboxData[];
    onGroupEditorClose: () => void;
    onGroupEditorModalVisibilityChange: (isShown: boolean) => void;
    renameGroupEditor: () => Promise<void>;
    onGroupEditorRenameSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onGroupEditorHeaderCheckboxChange: (checked: boolean) => void;
    onGroupEditorCheckboxChange: (checked: boolean, item: TItem) => void;
    onUngroupSelected: () => Promise<void>;
    groupEditorColumns: TableColumn<TItem>[];
    totalLabel: string;
    emptyStateLabel: string;
};

export const GroupEditorModalWrapper = <TItem, TTarget extends GroupEditorModalWrapperTarget>({
    openedGroupEditor,
    groupEditorData,
    groupEditorNameDraft,
    setGroupEditorNameDraft,
    isGroupEditorLoading,
    groupEditorError,
    pickedItems,
    page,
    setPage,
    rowsOnPage,
    onRowsOnPageUpdate,
    isGroupEditorNameSaving,
    isGroupEditorUngrouping,
    normalizedGroupEditorNameDraft,
    hasGroupEditorNameChanged,
    totalRows,
    pageCount,
    pagedItems,
    isGroupEditorHeaderChecked,
    groupEditorCheckboxData,
    onGroupEditorClose,
    onGroupEditorModalVisibilityChange,
    renameGroupEditor,
    onGroupEditorRenameSubmit,
    onGroupEditorHeaderCheckboxChange,
    onGroupEditorCheckboxChange,
    onUngroupSelected,
    groupEditorColumns,
    totalLabel,
    emptyStateLabel,
}: GroupEditorModalWrapperProps<TItem, TTarget>) => {
    if (!openedGroupEditor) {
        return null;
    }

    return (
        <GroupEditorModal
            isShown={true}
            onModalVisibilityChange={onGroupEditorModalVisibilityChange}
            title={`Редактирование группы: ${openedGroupEditor.name}`}
            isLoading={isGroupEditorLoading}
            loadingLabel="Загрузка группы..."
            error={groupEditorError}
            hasData={!!groupEditorData}
            groupNameLabel="Название группы"
            groupNameValue={groupEditorNameDraft}
            onGroupNameChange={setGroupEditorNameDraft}
            onGroupNameSubmit={onGroupEditorRenameSubmit}
            onGroupNameSave={() => {
                void renameGroupEditor();
            }}
            isGroupNameSaving={isGroupEditorNameSaving}
            canSaveGroupName={!!normalizedGroupEditorNameDraft && hasGroupEditorNameChanged}
            saveGroupNameLabel="Сохранить имя"
            saveGroupNamePendingLabel="Сохраняем..."
            totalLabel={totalLabel}
            totalValue={totalRows}
            selectedLabel="Выбрано"
            selectedValue={pickedItems.length}
            tableColumns={groupEditorColumns}
            tableData={pagedItems}
            pickedData={pickedItems}
            onHeaderCheckboxChange={onGroupEditorHeaderCheckboxChange}
            onCheckboxChange={onGroupEditorCheckboxChange}
            isHeaderChecked={isGroupEditorHeaderChecked}
            checkboxData={groupEditorCheckboxData}
            emptyStateLabel={emptyStateLabel}
            pageCount={pageCount}
            page={page}
            setPage={setPage}
            rowsOnPage={rowsOnPage}
            onRowsOnPageUpdate={onRowsOnPageUpdate}
            ungroupLabel="Разгруппировать"
            ungroupPendingLabel="Разгруппируем..."
            isUngrouping={isGroupEditorUngrouping}
            canUngroup={pickedItems.length > 0}
            onUngroup={onUngroupSelected}
            onClose={onGroupEditorClose}
            closeLabel="Закрыть"
        />
    );
};
